import {
  FeatureFlagCategory,
  FeatureFlagAvailability,
  FeatureFlagType,
} from 'modules/common/drawers/featureFlagDrawer/types';
import { isDefaultIntegration } from 'modules/settings/integrations/managedIntegrations/utils';

import { FeatureFlagData } from '../types';
import {
  minRequiredNumberValidation,
  numberValidation,
} from './featureFlagValidators';

export enum FeatureFlag {
  DdtcRegistered = 'ddtcRegistered',
  FileUploadSizeLimit = 'fileUploadSizeLimit',
  IsNewGrowthCustomer = 'isNewGrowthCustomer',
  IsQuotingToolAvailable = 'isQuotingToolAvailable',
  MinQuoteNumber = 'minQuoteNumber',
  MinOrderNumber = 'minOrderNumber',
  IsIncludingQuotePdfAvailable = 'isIncludingQuotePdfAvailable',
  IsQuotePriorityAvailable = 'isQuotePriorityAvailable',
  IsCalculatedShippingEnabled = 'isCalculatedShippingEnabled',
  MaxQuoteItemQuantities = 'maxQuoteItemQuantities',
  CustomTablesCountLimit = 'customTablesCountLimit',
  UploadComponentCountLimit = 'uploadComponentCountLimit',
  IsAssembliesEnabled = 'isAssembliesEnabled',
  IsLinearMetalNestingEnabled = 'isLinearMetalNestingEnabled',
  IsMultiComponentNestingEnabled = 'isMultiComponentNestingEnabled',
  IsAdvancedNestingEnabled = 'isAdvancedNestingEnabled',
  IsPdfVectorizationEnabled = 'isPdfVectorizationEnabled',
  IsWorkflowsAvailable = 'isWorkflowsAvailable',
  IsTasksAvailable = 'isTasksAvailable',
  MaxWorkflowSteps = 'maxWorkflowSteps',
  IsAdvancedAnalyticsEnabled = 'isAdvancedAnalyticsEnabled',
  IsMostLikelyWonQuantityEnabled = 'isMostLikelyWonQuantityEnabled',
  MaxSupplierFacilities = 'maxSupplierFacilities',
  IsErpStoresEnabled = 'isErpStoresEnabled',
  IsApiExportControlled = 'isApiExportControlled',
  IsErpCodeAvailable = 'isErpCodeAvailable',
  IsMetadataUiEnabled = 'isMetadataUiEnabled',
  IsPmiRenderingEnabled = 'isPmiRenderingEnabled',
  IsCustomPartAttrsEnabled = 'isCustomPartAttrsEnabled',
  IsCustomProcessesEnabled = 'isCustomProcessesEnabled',
  IsItModuleEnabled = 'isItModuleEnabled',
  IsIntegrationsUiEnabled = 'isIntegrationsUiEnabled',
  MaxManagedIntegrations = 'maxManagedIntegrations',
  IsPennAppEnabled = 'isPennAppEnabled',
  IsTkLivePricingEnabled = 'isTkLivePricingEnabled',
  IsOmLivePricingEnabled = 'isOmLivePricingEnabled',
  IsEnforceMfaOrSsoForShareRecipientsFeatureFlagEnabled = 'isEnforceMfaOrSsoForShareRecipientsFeatureFlagEnabled',
  IsBomBuilderEnabled = 'isBomBuilderEnabled',
  IsLegacyProcessAndMaterialWorkflowEnabled = 'isLegacyProcessAndMaterialWorkflowEnabled',
  IsCloudConnectorEnabled = 'isCloudConnectorEnabled',
  IsMscChatAvailable = 'isMscChatAvailable',
  IsCustomUploadFlowAvailable = 'isCustomUploadFlowAvailable',
  IsPdfRedactionEnabled = 'isPdfRedactionEnabled',
  IsPemInsertionWarningsEnabled = 'isPemInsertionWarningsEnabled',
  IsJobsEnabled = 'isJobsEnabled',
  IsCustomMarkupsEnabled = 'isCustomMarkupsEnabled',
  HideLongQuoteItemLists = 'hideLongQuoteItemLists',
  IsPartViewerSimilarPartsAvailable = 'isPartViewerSimilarPartsAvailable',
  IsNewSimilarPartsEnabled = 'isNewSimilarPartsEnabled',
  QuickbooksExportCreditCardOrderAfterPartsShipped = 'quickbooksExportCreditCardOrderAfterPartsShipped',
  IsNewVectorizationEnabled = 'isNewVectorizationEnabled',
  IsAnalyticsTimezoneEnabled = 'isAnalyticsTimezoneEnabled',
  IsBeamsEnabled = 'isBeamsEnabled',
  IsEnhancedPartbotEnabled = 'isEnhancedPartbotEnabled',
  RunesApiVersion = 'runesApiVersion',
  ModernFileStatusingPercentage = 'modernFileStatusingPercentage',
  IsRequirementsReviewEnabled = 'isRequirementsReviewEnabled',
  IsMultisiteFunctionalityAvailable = 'isMultisiteFunctionalityAvailable',
  IsCriticalPathLeadTimeEnabled = 'isCriticalPathLeadTimeEnabled',
  IsFusionIntegrationEnabled = 'isFusionIntegrationEnabled',
  Cp3ProcessingPercentage = 'cp3ProcessingPercentage',
  IsFlatPatternExportEnabled = 'isFlatPatternExportEnabled',
  IsEauFieldEnabled = 'isEauFieldEnabled',
}

export const featureFlagsStaticData = {
  [FeatureFlag.DdtcRegistered]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    explanation:
      'Enables the ITAR checkbox functionality, which a user can select for a part. Once this is on, it is up to the shop to enforce their team to mark the correct parts as ITAR. Marking a part as ITAR will mark any quotes it is in as ITAR',
    displayName: 'Has ITAR compliance',
    default: true,
  },
  [FeatureFlag.FileUploadSizeLimit]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: 250, upsell: false },
      pro: { default: 250, upsell: false },
      ent: { default: 250, upsell: false },
      entMs: { default: 250, upsell: false },
    },
    contracted: false,
    explanation:
      'Determines the maximum file upload size, such that files larger than this limit will fail to upload. Can be increased this to 250mb for any customer, but exclusively allowed to be 1000mb (1gb) for RCO Engineering.',
    displayName: 'Maximum file upload size (in MB)',
    default: 150,
    min: 0,
  },
  [FeatureFlag.IsNewGrowthCustomer]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'Shows Paperless Parts branding on the digital quote, quote PDF, SmartRFQ form, and emails generated by PP sent to buuyers. Turn on for growth customers sold after 1/1/2023.',
    displayName: 'Is featureset for new growth customers enabled',
    default: false,
  },
  [FeatureFlag.IsQuotingToolAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    explanation:
      'Whether the complete quoting toolset is available to the account. Differentiates a paying customer quoting account from a free part-viewer-only account',
    displayName: 'Is quoting tool available',
    default: false,
  },
  [FeatureFlag.MinQuoteNumber]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: 0, upsell: false },
      pro: { default: 0, upsell: false },
      ent: { default: 0, upsell: false },
      entMs: { default: 0, upsell: false },
    },
    contracted: false,
    explanation:
      'The lowest Quote number for quotes created in the user group.  Intended to help shops match / take-over from pre-existing quote numbering.  Limited to a number larger than the current highest quote number and increments by 1.',

    displayName: 'Minimum quote number',
    default: 0,
    min: 0,
    nullable: true,
    getSchema: (featureFlags) => {
      if (featureFlags?.minQuoteNumber != null) {
        return minRequiredNumberValidation(
          featureFlags.minQuoteNumber as number,
        );
      } else {
        return numberValidation;
      }
    },
  },
  [FeatureFlag.MinOrderNumber]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: 0, upsell: false },
      pro: { default: 0, upsell: false },
      ent: { default: 0, upsell: false },
      entMs: { default: 0, upsell: false },
    },
    contracted: false,
    explanation:
      'The lowest Order number for orders created in the user group. Intended to help shops match / take-over from pre-existing order numbering.  Limited to a number larger than the current highest order number and increments by 1.',
    displayName: 'Minimum order number',
    default: 0,
    min: 0,
    nullable: true,
    getSchema: (featureFlags) => {
      if (featureFlags?.minOrderNumber != null) {
        return minRequiredNumberValidation(
          featureFlags.minOrderNumber as number,
        );
      } else {
        return numberValidation;
      }
    },
  },
  [FeatureFlag.IsIncludingQuotePdfAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    explanation:
      'Whether the "include quote PDF in email" checkbox is present in the quote finalize workflow, which will default to checked. Ask the customer how they want to send quotes (Digital quote, quote PDF, mark as sent, etc.) and whether they expect to use our quote received tracking--a potential downside of including the quote PDF in the email is if the buyer only opens the PDF, the shop will not see when their buyer viewed the quote. Note: if the shop is concerned about checkout on the Digital Quote, they can turn off checkout for all or specific accounts.',
    displayName: 'Is "include quote PDF in email" checkbox available',
    default: true,
  },
  [FeatureFlag.IsQuotePriorityAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    deprecate: 'Flag to be deprecated in favor of a user setting',
    explanation:
      'Whether the "quote priority" dropdown is present in the BAQ header section. This is a numeric field, with options 1-10, that default to nothing. The field is also shown on the Quotes Dashboard and can be used to filter or sort quotes.  Defaults to off, optional to turn on for all tiers.',
    displayName: 'Is quote priority available',
    default: true,
  },
  [FeatureFlag.IsCalculatedShippingEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      "Do not turn this on for any account. Enables an automated shipping calculation on the Digital Quote, which is calculated using the weight of the part (so will not work for pdfs or manual parts) and the current USPS / UPS shipping rates. This is confusing, often wrong (unclear how it picks the shipping box size, doesn't show how it arrives at a number), and risky for the shop (could lead to them losing money).",
    displayName: 'Is calculated shipping available',
    default: true,
  },
  [FeatureFlag.MaxQuoteItemQuantities]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: 4, upsell: false },
      pro: { default: 8, upsell: false },
      ent: { default: 15, upsell: false },
      entMs: { default: 15, upsell: false },
    },
    contracted: false,
    explanation:
      'The maximum number of quantity breaks that can be added to a quote item.',
    displayName: 'Maximum quote item quantities',
    default: 8,
    min: 0,
  },
  [FeatureFlag.CustomTablesCountLimit]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: 5, upsell: false },
      pro: { default: 5, upsell: false },
      ent: { default: 5, upsell: false },
      entMs: { default: 5, upsell: false },
    },
    contracted: false,
    explanation:
      'The maximum number of custom tables a user group can have.  No strict limits, but large custom table haves a non-zero cost and limits ensure we are not overwhelmed unintentionally.',
    displayName: 'Custom table count limit',
    default: 5,
    min: 0,
  },
  [FeatureFlag.UploadComponentCountLimit]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: 500, upsell: false },
      pro: { default: 1000, upsell: false },
      ent: { default: 1000, upsell: false },
      entMs: { default: 1000, upsell: false },
    },
    contracted: false,
    explanation:
      "The number of unique components we will automatically extract from a single assembly CAD file upload. If an assembly exceeds this limit, the file will drop in as a single BOM item and we won't break out the components. Note that even at its maximum, there will be other limiting factors (like file size, actions timing out) that will make these files difficult to work with in the platform.",
    displayName: 'Upload component limit',
    default: 500,
    min: 0,
  },
  [FeatureFlag.IsAssembliesEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    deprecate: 'Flag to be deprecated',
    learnMoreLink:
      'https://metabase.paperlessparts.com/question/4918-growth-accounts-sending-quotes-with-mfg-children',
    explanation:
      'Enables assembly functionality (breaking out CAD files with multiple components, adding hardware and manufactured child components, manually manipulating BOM structure). Defaults to enabled, leave enabled for all accounts. Note: In the new pricing tiers, growth accounts are only allowed to add hardware to a manufactured component, and are technically not allowed to add manufactured components as children. However, in the product today, this feature flag must be enabled in order to add any child component (manufactured or purchased). Until that changes, try to avoid training growth customers in adding manufactured components as children. If a growth customer asks or needs to based on the files they get, raise to Scott Mahoney or Jon Dorr to determine if there is value in upselling the customer to pro. We will be tracking the number of quote items sent with manufactured child components, found at the linked URL.',
    displayName: 'Is assemblies enabled',
    default: true,
  },
  [FeatureFlag.IsLinearMetalNestingEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/PROD/blog/2022/12/09/394526744/12+16+Multi-Component+Linear+Nesting',
    deprecate: 'Flag to be deprecated in favor of a user setting',
    explanation:
      'Enables multi-component linear nesting.  Requires accompanying specific P3L configuration found in TIS-made new shop operation templates.  Only turn on the flag after setting up the P3L for product tracking reasons.',
    displayName: 'Is multi component linear nesting enabled',
    default: false,
  },
  [FeatureFlag.IsMultiComponentNestingEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/PROD/pages/859799635/11+15+Multi-Component+Sheet+Metal+Nesting',
    deprecate: 'Flag to be deprecated in favor of a user setting',
    explanation:
      'Enables multi-component sheet metal nesting.  Requires accompanying specific P3L configuration found in TIS-made new shop operation templates.  Only turn on the flag after setting up the P3L for product tracking reasons.',
    displayName: 'Is multi component sheet nesting enabled',
    default: true,
  },
  [FeatureFlag.IsAdvancedNestingEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    deprecate:
      'To be deprecated long-term in favor of stronger single-component nesting with parts-per-sheet in the multi-component nesting module',
    contracted: false,
    explanation:
      'Enables legacy single-component sheet metal nesting. As much as possible, customers should be funnelled into our multi-component sheet metal nesting feature instead; but there are some edge cases around parts-per-sheet nesting where this feature is still needed.',
    displayName: 'Is legacy single component sheet nesting enabled',
    default: true,
  },
  [FeatureFlag.IsPdfVectorizationEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    deprecate: 'Flag to be deprecated',
    explanation: 'Enables the PDF vectoriation and DXF editing workflows.',
    displayName: 'Is PDF vectorization enabled',
    default: true,
  },
  [FeatureFlag.IsWorkflowsAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: true },
      ent: { default: true, upsell: true },
      entMs: { default: true, upsell: true },
    },
    contracted: false,
    explanation:
      'Enables custom workflows dropdowns, which the shop can customize under Configure --> Workflows. Available as an add-on for Pro (should be included on the PO as Custom Workflows)',
    displayName: 'Is advanced workflows enabled',
    default: true,
  },
  [FeatureFlag.IsTasksAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: true },
      ent: { default: true, upsell: true },
      entMs: { default: true, upsell: true },
    },
    contracted: false,
    explanation:
      'Enables the tasks assignment tool. Available as an add-on for Pro, tied with workflows (should be included on the PO as Custom Workflows)',
    displayName: 'Is tasks enabled',
    default: true,
  },
  [FeatureFlag.MaxWorkflowSteps]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: 10, upsell: false },
      pro: { default: 10, upsell: false },
      ent: { default: 10, upsell: false },
      entMs: { default: 10, upsell: false },
    },
    contracted: false,
    deprecate: 'Flag to be deprecated',
    explanation:
      'The maxmimum number of workflow steps the shop can create.  No strict limits.',
    displayName: 'Maximum number of workflow steps',
    default: 10,
    min: 0,
  },
  [FeatureFlag.IsAdvancedAnalyticsEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: true },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    explanation:
      'Enables the advanced analytics tool and the ability to create custom dashboards and queries. Available as an add-on for Pro (should be included on the PO as Advanced Analytics)',
    displayName: 'Is advanced analytics enabled',
    default: true,
  },
  [FeatureFlag.IsMostLikelyWonQuantityEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckInstructions,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    explanation:
      'Enables the most likely won quantity checkbox and percent in the line item quantities drawer. Theoretically used to forecast pipeline based on which quantity is most likely to be won--in analytics, a shop can use "component quantities is most likely won quantity" and "component quantities is most likely won quantity percent". Initially created as one-off for an enterprise customer but can be enabled only if a shop brings up wanting this.',
    displayName: 'Is most likely won quantity enabled',
    default: true,
  },
  [FeatureFlag.MaxSupplierFacilities]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: 0, upsell: false },
      pro: { default: 0, upsell: false },
      ent: { default: 0, upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    explanation:
      'The maximum number of supplier facilities a user group can have.  Facilities can be changed on a quote level, which determines what appears on the Digital Quote as the sent-from facility and the facility logo. This feature was built as our initial multi-site solution, which is intended to be replaced by our user group collections / account switching functionality with cross-faciltiy dashboards eventually.',
    displayName: 'Maximum supplier facilities',
    default: 5,
    min: 1,
  },
  [FeatureFlag.IsErpStoresEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/PROD/blog/2023/02/16/463110145',
    explanation:
      'Enables repeat work with ERP data. If the ERP integration has not yet been configured to import historical work, enabling this will have no effect. Turn on for customers with integrations that are going to be or are already set up with repeat work.',
    displayName: 'Is ERP stores enabled',
  },
  [FeatureFlag.IsApiExportControlled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/PROD/pages/2087321601/CUI+ITAR+in+Paperless+and+Integrations',
    explanation:
      'Whether our API includes a direct file download link for parts marked as export-controlled.  All other files are already and always direct downloadable. Leave this flag off unless a shop specifically asks to be able to download all of their files from the API (including export-controlled). This should be very rare, and requires the shop to have an API token and be quoting ITAR.',
    displayName: 'Is API export controlled',
  },
  [FeatureFlag.IsErpCodeAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    explanation:
      'Adds an "ERP code" field to all team members in a user group, and to the multi-component nesting modules. ERP Code will be displayed on all accounts and operations in a user group even if this flag is off. Turn this on if a shop has an integration.',
    displayName: 'Is ERP code available',
  },
  [FeatureFlag.IsMetadataUiEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'Indicates whether the metadata key/value pairs can be edited in the UI for metadata-enabled objects. This was a custom feature for Fathom. Do NOT turn this on without consulting the Product team.',
    displayName: 'Is Metadata UI Enabled',
    default: true,
  },
  [FeatureFlag.IsPmiRenderingEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://help.paperlessparts.com/s/article/mbd-support-viewing-pmi-june-27th-2022',
    explanation:
      'Enables Product Manufacturing Information to be seen on the part viewer if available As part of the updated pricing tiers, growth customers cannot see PMI.',
    displayName: 'Is PMI rendering enabled',
    default: true,
  },
  [FeatureFlag.IsCustomPartAttrsEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://help.paperlessparts.com/article/42-p3l-language-features',
    explanation:
      'Enables custom part attributes, which are top-level properties stored on a part that you can access in P3L. Default custom part attributes can be created in the settings page.  As part of the updated pricing tiers, growth customers cannot use custom part attributes.',
    displayName: 'Is custom part attributes enabled',
    default: true,
  },
  [FeatureFlag.IsCustomProcessesEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://help.paperlessparts.com/article/125-custom-operation-generation',
    explanation:
      'Enables custom processes, which use geometric and / or user inputting information to dynamically generate operations for a part. As part of the updated pricing tiers, growth customers cannot use custom processes.',
    displayName: 'Is custom processes enabled',
    default: true,
  },
  [FeatureFlag.IsItModuleEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: true },
      ent: { default: false, upsell: true },
      entMs: { default: false, upsell: true },
    },
    contracted: false,
    explanation:
      'Only available for customers who have purchased the IT Module as an add-on (Prior to July 2023). Post July 2023, only available to customers on Pro or Enterprise who have purchased the Aerospace and Defense Package (SUB-AD-PRO/ENT)',
    displayName: 'Is IT Module enabled',
    default: false,
  },
  [FeatureFlag.IsIntegrationsUiEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    deprecate: 'To be deprecated',
    explanation:
      'Indicates whether the Integration Manager UI is enabled, defaults to True. This is only set to False for customers who had API tokens but did not have integrations before the Integration Manager UI was introduced. When this flag is set to False, customers will still see the legacy "API Token" entry in the left hand side bar of the Settings page. In the near future, we will set this to True for all customers and remove this feature flag.',
    displayName: 'Is integrations UI enabled',
    default: true,
  },
  [FeatureFlag.MaxManagedIntegrations]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: 0, upsell: true },
      pro: { default: 1, upsell: true },
      ent: { default: 2, upsell: true },
      entMs: { default: 2, upsell: true },
    },
    contracted: false,
    explanation:
      'The maximum number of integrations a user group can manage via the Integration Manager UI. Note that in order to get an API token, a customer must have an Integration (each Integration comes with one API Token).',
    displayName: 'Maximum number of integrations',
    default: 5,
    min: 0,
    getSchema: (_featureFlags, managedIntegrations) => {
      const nonDefaultIntegrations = managedIntegrations?.filter(
        (integration) => !isDefaultIntegration(integration),
      );
      return minRequiredNumberValidation(nonDefaultIntegrations?.length ?? 0);
    },
  },
  [FeatureFlag.IsPennAppEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    explanation: 'Enables Penn Connect, on for all tiers',
    displayName: 'Is PEMConnect enabled',
    default: true,
  },
  [FeatureFlag.IsTkLivePricingEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckInstructions,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/BF/pages/471498786/Thyssenkrupp+OnlineMetals+Real-Time+Pricing+Onboarding+Process',
    contracted: false,
    explanation:
      'Gives user access to the Real Time Pricing Operation and get quotes from TK.  In open beta with careful elligibility requirements; see the linked instructions for more info.',
    displayName: 'Is Thyssenkrupp live pricing enabled',
    default: false,
  },
  [FeatureFlag.IsOmLivePricingEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckInstructions,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/BF/pages/471498786/Thyssenkrupp+OnlineMetals+Real-Time+Pricing+Onboarding+Process',
    contracted: false,
    explanation:
      'Gives user access to the Real Time Pricing Operation and get quotes from Online Metals. In open beta with careful elligibility requirements; see the linked instructions for more info.',
    displayName: 'Is Online Metals live pricing enabled',
    default: false,
  },
  [FeatureFlag.IsEnforceMfaOrSsoForShareRecipientsFeatureFlagEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'Allows an organization to require a recipient of an externally shared file to only be able to view the file if they have MFA or SSO enabled. In beta, leave unchecked, consult R&D to learn more.',
    displayName: 'Enforce MFA or SSO for share recipients',
    default: false,
  },
  [FeatureFlag.IsBomBuilderEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: true,
    explanation:
      'Includes the BOM Builder interface and BOM suggestions. Should be enabled for contracted Pro and Ent customers.',
    displayName: 'Is BOM Builder enabled',
    default: false,
  },
  [FeatureFlag.IsLegacyProcessAndMaterialWorkflowEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    explanation:
      'Maintains the current user behavior for selecting process, material, and finish. Disabling the feature flag unlocks an updated selection of those fields intended to streamline part information entry with quote setup; however, this created issues for the user experience with custom processes referencing material. Leave checked.',
    displayName: 'Is legacy process and material workflow enabled',
    default: false,
  },
  [FeatureFlag.IsCloudConnectorEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Configuration,
    availability: FeatureFlagAvailability.CheckInstructions,
    tierInfo: {
      growth: { upsell: false },
      pro: { upsell: false },
      ent: { upsell: false },
      entMs: { upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/PROD/pages/2185986051/Integration+Connector+Rollout',
    explanation:
      'Exposes a link in the Integrations settings page for a user to download the Cloud Connector.  Carefully review the pre-requisites and follow the included instructions the linked guide before enabling.',
    displayName: 'Is integration connector (IC) enabled',
    default: true,
  },
  [FeatureFlag.IsMscChatAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    explanation: 'Enables MSC Techmate portal, on for all tiers',
    displayName: 'Is MSC chat available',
    default: true,
  },
  [FeatureFlag.IsCustomUploadFlowAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'Special legacy in-app form built for Boston Centerless. Leave unchecked for all accounts other than Boston Centerless.',
    displayName: 'Is Boston Centerless upload flow available',
    default: false,
  },
  [FeatureFlag.IsPdfRedactionEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    deprecate: 'Flag to be deprecated',
    explanation:
      'Removed from the backend, yet to be removed from the frontend.',
    displayName: 'Is PDF redaction enabled',
    default: true,
  },
  [FeatureFlag.IsPemInsertionWarningsEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: true, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    deprecate: 'Flag to be deprecated',
    explanation: 'Turned on for most users. Leave checked.',
    displayName: 'Is PEM insertion warnings enabled',
    default: false,
  },
  [FeatureFlag.IsJobsEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'Turns on different matching part UI. Leave unchecked. Some customers have this checked, but the interface is buggy.',
    displayName: 'Is jobs enabled',
    default: true,
  },
  [FeatureFlag.IsCustomMarkupsEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: true,
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/PROD/pages/1165164551/3+27+Custom+Markups+Margins',
    explanation:
      'Enables custom markups, a variant of pricing items that allows custom aggregation of cost.  Also unlocks iterating through the BOM in all pricing item P3L.',
    displayName: 'Is custom markups enabled',
    default: false,
  },
  [FeatureFlag.HideLongQuoteItemLists]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'Created as part of the updated BAQ project. Turning on hiding the quote item list for large quotes. Always unchecked, ignore',
    displayName: 'Is quote item list hidden for large quotes',
    default: true,
  },
  [FeatureFlag.IsPartViewerSimilarPartsAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: true,

    learnMoreLink:
      'https://help.paperlessparts.com/s/article/similar-parts-search-beta',
    explanation:
      'In private beta described in the linked article.  Reach out to R&D if a shop mentions wanting a feature like this, we may be able to turn this on for them.',
    displayName: 'Is partviewer similar parts available',
    default: true,
  },
  [FeatureFlag.IsNewSimilarPartsEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Development,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation: 'Private beta.',
    displayName: 'Is new similar parts enabled',
    default: false,
  },
  [FeatureFlag.QuickbooksExportCreditCardOrderAfterPartsShipped]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'This is a placeholder feature for a strategic customer. This feature flag changes the trigger point for when the QuickBooks integration creates an invoice in QuickBooks for credit card orders. When this is false, the invoice will be created as soon as the order is created in Paperless Parts. When this is true, the invoice will be created when the "Ship Parts + Collect Payout" action is performed on the order.',
    displayName: 'QuickBooks export credit card order after parts shipped',
    default: false,
  },
  [FeatureFlag.IsNewVectorizationEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    learnMoreLink: 'https://paperlessparts.productboard.com/detail/17849546',
    explanation:
      "In beta described in the linked product board item; likely broken now.  A visual and capability upgrade to our PDF vectorization toolset that we're testing with Customer Advisory Board members, owned by Euclid team. We are pushing off plans to release this widely until we can include its same quality-of-life improvements to DXF viewing in the normal part/file viewer. Reach out to Hank P. if a shop is frustrated with the legacy PDF vectorization feature.",
    displayName: 'Is new vectorization enabled',
    default: false,
  },
  [FeatureFlag.IsAnalyticsTimezoneEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'A half-complete implementation of using user-setting-based time zone in analytics.  Built for use by one shop, Athena3D, which has since churned.  No plans to complete implementation at this time.',
    displayName: 'Is analytics timezone enabled',
    default: false,
  },
  [FeatureFlag.IsBeamsEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: true,
    explanation:
      'Whether the file processing that drives BOM table suggestions.',
    displayName: 'Is BEAMS enabled',
    default: false,
  },
  [FeatureFlag.IsEnhancedPartbotEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: true,

    explanation:
      'Extraction of GD&T and callouts from prints, highlighted in the viewer.  Required to enable Requirements Review.',
    displayName: 'Is GD&T and callouts extractions enabled',
    default: false,
  },
  [FeatureFlag.RunesApiVersion]: {
    type: FeatureFlagType.String,
    category: FeatureFlagCategory.Development,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: '1.0.0', upsell: false },
      pro: { default: '1.0.0', upsell: false },
      ent: { default: '1.0.0', upsell: false },
      entMs: { default: '1.0.0', upsell: false },
    },
    contracted: false,
    explanation: '',
    displayName: 'Runes API version',
    default: '1.0.0',
    allowedValues: ['1.0.0', '2.0.0'],
  },
  [FeatureFlag.ModernFileStatusingPercentage]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: 0, upsell: false },
      pro: { default: 0, upsell: false },
      ent: { default: 0, upsell: false },
      entMs: { default: 0, upsell: false },
    },
    contracted: false,
    deprecate: 'Feature to be deprecated',
    explanation:
      'Controls the rollout of improvements to file statusing--does not contain product functionality. Do not change manually for customer accounts.',
    displayName: 'Modern file statusing percentage (0-100)',
    default: 0,
    min: 0,
  },
  [FeatureFlag.IsRequirementsReviewEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: true,
    learnMoreLink:
      'https://help.paperlessparts.com/s/article/requirements-review',
    explanation:
      'Enables Requirements Review, a rule builder and workflow for flagging and reviewing critical requirements. One of two flags necessary to do so (also `are GD&T and callout extractions enabled`). Should be enabled for contracted Pro and Ent customers.',
    displayName: 'Is requirements review enabled',
    default: false,
  },
  [FeatureFlag.IsMultisiteFunctionalityAvailable]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Development,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'Feature flag for development on new multisite functionality. Leave unchecked.',
    displayName: 'Is multisite functionality available',
    default: false,
  },
  [FeatureFlag.IsCriticalPathLeadTimeEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.ConsultRD,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      "A private beta with a small number of customers that delivers assembly lead time rollup with a specific strategy. Leave unchecked. Available to be configured for customers in onboarding by @miles.barker or @colin.mcnamara (who are trained) or by consulting directly with @Hank Portney--not rolled out to a broader group because we're still evaluating its completeness and promotability and preserving our effort for elsewhere.",
    displayName: 'Is critical path lead time enabled',
    default: false,
  },
  [FeatureFlag.IsFusionIntegrationEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Entitlement,
    availability: FeatureFlagAvailability.CheckEntitlement,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: true, upsell: false },
      ent: { default: true, upsell: false },
      entMs: { default: true, upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/PROD/pages/1709113394',
    deprecate:
      'Flag to be deprecated, but kept off for part viewer customers (quoting disabled)',
    explanation:
      'Our integration with Autodesk Fusion that allows browsing hubs, projects, folders and files as well as sbned files back and forth.  Note: not available to part viewer customers.',
    displayName: 'Is Autodesk Fusion integration enabled',
    default: false,
  },
  [FeatureFlag.Cp3ProcessingPercentage]: {
    type: FeatureFlagType.Number,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: 100, upsell: false },
      pro: { default: 100, upsell: false },
      ent: { default: 100, upsell: false },
      entMs: { default: 100, upsell: false },
    },
    contracted: false,
    learnMoreLink:
      'https://paperlessparts.atlassian.net/wiki/spaces/PROD/pages/760512555',
    explanation:
      'Feature flag originally used to control the rollout of the 250mb complex part processing pipeline (CP3) described in the attached link. Dictates the percent of parts from that user group randomly elected to move through the new pipeline. All shops should be at 100 now.',
    displayName: 'CP3 Processing Percentage (0-100)',
    default: 0,
    min: 0,
  },
  [FeatureFlag.IsFlatPatternExportEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Backburner,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      'Enables exporting flat patterns from sheet metal interrogation via API and UI. Currently restricted to Laser Central only. Leave unchecked. NOTE: Comes with a number of caveats that make these patterns functionally unsuitable for use in production manufacturing. Reach out to @Hank Portney, mentioning this note on this page, for more information or to consider for another customer.',
    displayName: 'Allow exporting flat patterns',
    default: false,
  },
  [FeatureFlag.IsEauFieldEnabled]: {
    type: FeatureFlagType.Boolean,
    category: FeatureFlagCategory.Development,
    availability: FeatureFlagAvailability.NotAvailable,
    tierInfo: {
      growth: { default: false, upsell: false },
      pro: { default: false, upsell: false },
      ent: { default: false, upsell: false },
      entMs: { default: false, upsell: false },
    },
    contracted: false,
    explanation:
      '(No significance provided in the text, keeping checkbox FALSE)',
    displayName: 'Is EAU field enabled',
    default: false,
  },
} as const satisfies Record<FeatureFlag, FeatureFlagData>;
